import { Navigate, Outlet, useParams } from 'react-router-dom';

import type { NavItemConfig, RouteNavConfig } from '@revelio/auth';
import { PagePaths, PageTitles } from '@revelio/core';
import { Tab } from '@revelio/data-access';

import { CustomDashRoute } from '../CustomDash/CustomDashRoute';
import { TextChartHelpPage } from '../CustomDash/components/Chart/TextChart/TextChartHelpPage';
import { activityComposition } from '../CustomDash/templates/activity/activity-composition';
import { activityMarketConditions } from '../CustomDash/templates/activity/activity-market-conditions';
import { activityPay } from '../CustomDash/templates/activity/activity-pay';
import { activityPostings } from '../CustomDash/templates/activity/activity-postings';
import { companyComposition } from '../CustomDash/templates/company/company-composition';
import { companyDiversity } from '../CustomDash/templates/company/company-diversity';
import { companyOverview } from '../CustomDash/templates/company/company-overview';
import { companyPay } from '../CustomDash/templates/company/company-pay';
import { companyPostings } from '../CustomDash/templates/company/company-postings';
import { companyScreener } from '../CustomDash/templates/company/company-screener';
import { companySentiment } from '../CustomDash/templates/company/company-sentiment';
import { companySubsidiaries } from '../CustomDash/templates/company/company-subsidiaries';
import { companyTransitions } from '../CustomDash/templates/company/company-transitions';
import { postingDiscovery } from '../CustomDash/templates/discovery/posting-discovery';
import { talentDiscovery } from '../CustomDash/templates/discovery/talent-discovery';
import { geographyComposition } from '../CustomDash/templates/geo/geography-composition';
import { geographyMarketConditions } from '../CustomDash/templates/geo/geography-market-conditions';
import { geographyOverview } from '../CustomDash/templates/geo/geography-overview';
import { geographyPay } from '../CustomDash/templates/geo/geography-pay';
import { geographyPostings } from '../CustomDash/templates/geo/geography-postings';
import { geographyScreener } from '../CustomDash/templates/geo/geography-screener';
import { geographySentiment } from '../CustomDash/templates/geo/geography-sentiment';
import { geographyTransitions } from '../CustomDash/templates/geo/geography-transitions';
import { industryComposition } from '../CustomDash/templates/industry/industry-composition';
import { industryMarketConditions } from '../CustomDash/templates/industry/industry-market-conditions';
import { industryPay } from '../CustomDash/templates/industry/industry-pay';
import { industryPostings } from '../CustomDash/templates/industry/industry-postings';
import { industrySentiment } from '../CustomDash/templates/industry/industry-sentiment';
import { industryTransitions } from '../CustomDash/templates/industry/industry-transitions';
import { roleComposition } from '../CustomDash/templates/role/role-composition';
import { roleMarketConditions } from '../CustomDash/templates/role/role-market-conditions';
import { roleOverview } from '../CustomDash/templates/role/role-overview';
import { rolePay } from '../CustomDash/templates/role/role-pay';
import { rolePostings } from '../CustomDash/templates/role/role-postings';
import { roleScreener } from '../CustomDash/templates/role/role-screener';
import { roleSentiment } from '../CustomDash/templates/role/role-sentiment';
import { roleTransitions } from '../CustomDash/templates/role/role-transitions';
import { skillComposition } from '../CustomDash/templates/skill/skill-composition';
import { skillMarketConditions } from '../CustomDash/templates/skill/skill-market-conditions';
import { skillPay } from '../CustomDash/templates/skill/skill-pay';
import { skillPostings } from '../CustomDash/templates/skill/skill-postings';
import { supportedEntityTypes } from '../CustomDash/utils/routing-helpers';
import { DataBuilderRouteGuard } from '../DataBuilder/DataBuilderRouteGuard';
import { DataBuilderPaths } from '../DataBuilder/steps/data-builder-steps';
import { DeliverablesDataEnrichment } from '../deliverables/data-enrichment/DeliverablesDataEnrichment';
import { EntitySearch } from '../entity-search/entity-search';
import { EntitySearchResults } from '../entity-search/entity-search-results';
import DataDictionary from '../iframed-pages/data-dictionary';
import TaxonomyCatalog from '../iframed-pages/taxonomy-catalog';
import ReportPage from '../reports/report-page';
import ReportsPage from '../reports/reports-page';
import SampleReportPage from '../reports/sample-report.page';
import { ReportBuilderDeliverablePaths } from '../reports/utils/report-builder-paths';
import LzStringDecoderPage from '../tools/LzStringDecoderPage';
import SnowflakeQueryPlaygroundPage from '../tools/SnowflakeQueryPlaygroundPage';
import ToolsLandingPage from '../tools/ToolsLandingPage';
import { lazyNamedWithReload, lazyRouteElement } from './lazy-route';
import { RequireAuth } from './ProtectedRoute';
import {
  IconAnalyticsBars,
  IconApplicationsGrid,
  IconDataBuilder,
  IconDiscoveryCompass,
  IconReportsStack,
} from './side-nav/custom-icons';
import { iconUrls } from './side-nav/icons';

const adminPages = () => import('../adminRewrite/admin-route-pages');
const AdminRewrite = lazyNamedWithReload(adminPages, 'AdminRewrite');
const AdminRewriteUserEdit = lazyNamedWithReload(
  adminPages,
  'AdminRewriteUserEdit'
);
const AdminClientEdit = lazyNamedWithReload(adminPages, 'AdminClientEdit');
const AdminUserAdd = lazyNamedWithReload(adminPages, 'AdminUserAdd');
const ClientAdmin = lazyNamedWithReload(adminPages, 'ClientAdmin');
const UserEdit = lazyNamedWithReload(adminPages, 'UserEdit');

const dataBuilderPages = () =>
  import('../DataBuilder/data-builder-route-pages');
const DataBuilder = lazyNamedWithReload(dataBuilderPages, 'DataBuilder');
const DataBuilderStatus = lazyNamedWithReload(
  dataBuilderPages,
  'DataBuilderStatus'
);
const DataBuilderShareRedeem = lazyNamedWithReload(
  dataBuilderPages,
  'DataBuilderShareRedeem'
);
const DataBuilderChooseStep = lazyNamedWithReload(
  dataBuilderPages,
  'DataBuilderChooseStep'
);
const DataBuilderSavedDatasetsStep = lazyNamedWithReload(
  dataBuilderPages,
  'DataBuilderSavedDatasetsStep'
);
const DataBuilderDatasetStep = lazyNamedWithReload(
  dataBuilderPages,
  'DataBuilderDatasetStep'
);
const DataBuilderColumnsStep = lazyNamedWithReload(
  dataBuilderPages,
  'DataBuilderColumnsStep'
);
const DataBuilderDeliveryStep = lazyNamedWithReload(
  dataBuilderPages,
  'DataBuilderDeliveryStep'
);
const DataBuilderConfirmationStep = lazyNamedWithReload(
  dataBuilderPages,
  'DataBuilderConfirmationStep'
);

// Named so the template-launch spec can hold the builder's code: unnamed
// chunks get numeric ids in production builds.
const reportBuilderPages = () =>
  import(
    /* webpackChunkName: "report-builder" */ '../reports/report-builder/report-builder-route-pages'
  );
const ReportBuilderPage = lazyNamedWithReload(
  reportBuilderPages,
  'ReportBuilderPage'
);
const ReportType = lazyNamedWithReload(reportBuilderPages, 'ReportType');
const EntityConfiguration = lazyNamedWithReload(
  reportBuilderPages,
  'EntityConfiguration'
);
const ReportConfirmation = lazyNamedWithReload(
  reportBuilderPages,
  'ReportConfirmation'
);

const resumeEnrichmentPages = () =>
  import('../resume-enrichment/resume-enrichment-route-pages');
const ResumeEnrichment = lazyNamedWithReload(
  resumeEnrichmentPages,
  'ResumeEnrichment'
);
const EnrichedResumePage = lazyNamedWithReload(
  resumeEnrichmentPages,
  'EnrichedResumePage'
);

const HealthCheck = lazyNamedWithReload(
  () => import('../health-check/healthCheck'),
  'HealthCheck'
);

const FindEntityRedirect = () => {
  const { entityType, id } = useParams();
  return <Navigate to={`/tools/find/${entityType}/${id}`} replace />;
};

const sharedAppRoutesForNav: (RouteNavConfig | NavItemConfig)[] = [
  {
    name: PageTitles.DOCUMENTATION,
    icon: iconUrls.documentation,
    divider: true,
    isSourcesTab: false,
    children: [],
  },
  {
    name: 'Admin',
    path: 'admin/:clientId?',
    admin: true,
    element: lazyRouteElement(AdminRewrite),
  },
  {
    name: 'Admin User Edit',
    path: 'admin/user/:userId/edit',
    admin: true,
    clientAdmin: true,
    element: lazyRouteElement(AdminRewriteUserEdit),
  },
  {
    name: 'Admin Client Edit',
    path: 'admin/client/:clientId/edit',
    admin: true,
    clientAdmin: true,
    element: lazyRouteElement(AdminClientEdit),
  },
  {
    name: 'Admin User Add',
    path: 'admin/user/add/:clientId?',
    admin: true,
    clientAdmin: true,
    element: lazyRouteElement(AdminUserAdd),
  },
  {
    name: 'Manage',
    path: 'manage',
    clientAdmin: true,
    element: lazyRouteElement(ClientAdmin),
  },
  {
    name: 'Manage User Edit',
    path: 'manage/user/:userId/edit',
    element: lazyRouteElement(UserEdit),
  },
  {
    name: 'Taxonomy Catalog',
    path: `${PagePaths.TAXONOMY_CATALOG}/*`,
    excludeFromSideNavButtons: true,
    pageVisit: 'self',
    element: <TaxonomyCatalog />,
  },
  {
    name: 'Health Check',
    path: 'health-check',
    element: lazyRouteElement(HealthCheck),
    admin: true,
  },
  {
    name: 'Tools',
    path: 'tools',
    element: <Outlet />,
    excludeFromSideNavButtons: true,
    nestedRoutes: [
      {
        path: '',
        element: <ToolsLandingPage />,
      },
      {
        path: 'lzstring-decoder',
        element: <LzStringDecoderPage />,
      },
      {
        path: 'snowflake-query-playground',
        element: (
          <RequireAuth adminRequired>
            <SnowflakeQueryPlaygroundPage />
          </RequireAuth>
        ),
      },
      {
        path: 'find',
        element: <EntitySearch />,
      },
      {
        path: 'find/:entityType/:id',
        element: <EntitySearchResults />,
      },
    ],
  },
  {
    name: 'Text Chart Help',
    path: 'help/text-chart',
    element: <TextChartHelpPage />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Entity Search',
    path: '/find',
    element: <Navigate to="/tools/find" replace />,
    excludeFromSideNavButtons: true,
    // Legacy URL, redirected on sight — the page it lands on is the visit.
    pageVisit: 'none',
  },
  {
    name: 'Entity Search Page',
    path: '/find/:entityType/:id',
    element: <FindEntityRedirect />,
    excludeFromSideNavButtons: true,
    pageVisit: 'none',
  },
  // Generic custom dash routes
  ...supportedEntityTypes.map((entityType) => ({
    name: 'Custom Dashboard',
    path: `${entityType}/:templateId?/:action?`,
    // CustomDashRoute reports the page config's own id and type.
    pageVisit: 'self' as const,
    element: <CustomDashRoute />,
  })),
];

export const navItemsNewLayout: (RouteNavConfig | NavItemConfig)[] = [
  {
    name: PageTitles.DASHBOARD_NAV,
    icon: <IconAnalyticsBars />,
    isSourcesTab: true,
    // Every route below is a CustomDashRoute: it reports the page config's
    // own id and type, which the route path can't derive.
    pageVisit: 'self',
    // Visible rail items (Company / Geography / Role) come first; the
    // remaining per-view routes are registered with
    // `excludeFromSideNavButtons` so they're reachable by URL and still
    // count as Analytics for active-state matching, but they're surfaced
    // to the user via the page-level tab strip in the Header (see
    // AnalyticsViewTabs) instead of the rail.
    children: [
      {
        name: PageTitles.COMPANY,
        serverTab: [
          Tab.Overview,
          Tab.Transition,
          Tab.Posting,
          Tab.Compensation,
          Tab.Sentiment,
        ],
        path: PagePaths.COMPANY_SUMMARY,
        element: <CustomDashRoute idOverride={companyOverview.id} />,
        parent: PageTitles.DASHBOARD_NAV,
      },
      {
        name: PageTitles.INDUSTRY,
        serverTab: [
          Tab.Overview,
          Tab.Transition,
          Tab.Posting,
          Tab.Compensation,
          Tab.Sentiment,
        ],
        path: PagePaths.INDUSTRY_COMPOSITIONS,
        element: <CustomDashRoute idOverride={industryComposition.id} />,
        parent: PageTitles.DASHBOARD_NAV,
      },
      {
        name: PageTitles.GEOGRAPHY,
        serverTab: [
          Tab.Overview,
          Tab.Transition,
          Tab.Posting,
          Tab.Compensation,
          Tab.Sentiment,
        ],
        path: PagePaths.GEOGRAPHY_SUMMARY,
        element: <CustomDashRoute idOverride={geographyOverview.id} />,
        parent: PageTitles.DASHBOARD_NAV,
      },
      {
        name: PageTitles.ROLE,
        serverTab: [
          Tab.Overview,
          Tab.Transition,
          Tab.Posting,
          Tab.Compensation,
          Tab.Sentiment,
        ],
        path: PagePaths.ROLE_SUMMARY,
        element: <CustomDashRoute idOverride={roleOverview.id} />,
        parent: PageTitles.DASHBOARD_NAV,
      },
      // Skill unit of analysis. No overview page, so the rail entry lands on
      // Compositions directly.
      {
        name: PageTitles.SKILL,
        // The rail entry IS the Compositions route, so it carries that page's
        // gate — every other Compositions route and the tab strip use
        // Tab.Overview. (Role's rail entry spreads the tabs because it points
        // at the Overview page, which aggregates all of them.)
        serverTab: Tab.Overview,
        path: PagePaths.SKILL_COMPOSITIONS,
        element: <CustomDashRoute idOverride={skillComposition.id} />,
        parent: PageTitles.DASHBOARD_NAV,
      },
      // Activity unit of analysis: the same shape as Skill above.
      {
        name: PageTitles.ACTIVITY,
        // The rail entry IS the Compositions route, so it carries that gate.
        serverTab: Tab.Overview,
        path: PagePaths.ACTIVITY_COMPOSITIONS,
        element: <CustomDashRoute idOverride={activityComposition.id} />,
        parent: PageTitles.DASHBOARD_NAV,
      },
      {
        name: 'Company Compositions',
        serverTab: Tab.Overview,
        path: PagePaths.COMPANY_COMPOSITIONS,
        element: <CustomDashRoute idOverride={companyComposition.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Company Transitions',
        serverTab: Tab.Transition,
        path: PagePaths.COMPANY_TRANSITIONS,
        element: <CustomDashRoute idOverride={companyTransitions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Company Job Postings',
        serverTab: Tab.Posting,
        path: PagePaths.COMPANY_POSTINGS,
        element: <CustomDashRoute idOverride={companyPostings.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Company Pay',
        serverTab: Tab.Compensation,
        path: PagePaths.COMPANY_PAY,
        element: <CustomDashRoute idOverride={companyPay.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Company Diversity',
        serverTab: Tab.Overview,
        path: PagePaths.COMPANY_DIVERSITY,
        element: <CustomDashRoute idOverride={companyDiversity.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Company Sentiment',
        serverTab: Tab.Sentiment,
        path: PagePaths.COMPANY_SENTIMENT,
        element: <CustomDashRoute idOverride={companySentiment.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Company Corporate Structure',
        serverTab: Tab.Overview,
        path: PagePaths.COMPANY_SUBSIDIARIES,
        element: <CustomDashRoute idOverride={companySubsidiaries.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Industry Transitions',
        serverTab: Tab.Transition,
        path: PagePaths.INDUSTRY_TRANSITIONS,
        element: <CustomDashRoute idOverride={industryTransitions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Industry Job Postings',
        serverTab: Tab.Posting,
        path: PagePaths.INDUSTRY_POSTINGS,
        element: <CustomDashRoute idOverride={industryPostings.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Industry Pay',
        serverTab: Tab.Compensation,
        path: PagePaths.INDUSTRY_PAY,
        element: <CustomDashRoute idOverride={industryPay.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Industry Market Conditions',
        serverTab: { allOf: [Tab.Overview, Tab.Posting] },
        path: PagePaths.INDUSTRY_MARKET_CONDITIONS,
        element: <CustomDashRoute idOverride={industryMarketConditions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Industry Sentiment',
        serverTab: Tab.Sentiment,
        path: PagePaths.INDUSTRY_SENTIMENT,
        element: <CustomDashRoute idOverride={industrySentiment.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Compositions',
        serverTab: Tab.Overview,
        path: PagePaths.GEOGRAPHY_COMPOSITIONS,
        element: <CustomDashRoute idOverride={geographyComposition.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Transitions',
        serverTab: Tab.Transition,
        path: PagePaths.GEOGRAPHY_TRANSITIONS,
        element: <CustomDashRoute idOverride={geographyTransitions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Job Postings',
        serverTab: Tab.Posting,
        path: PagePaths.GEOGRAPHY_POSTINGS,
        element: <CustomDashRoute idOverride={geographyPostings.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Pay',
        serverTab: Tab.Compensation,
        path: PagePaths.GEOGRAPHY_PAY,
        element: <CustomDashRoute idOverride={geographyPay.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Market Conditions',
        serverTab: { allOf: [Tab.Overview, Tab.Posting] },
        path: PagePaths.GEOGRAPHY_MARKET_CONDITIONS,
        element: <CustomDashRoute idOverride={geographyMarketConditions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Sentiment',
        serverTab: Tab.Sentiment,
        path: PagePaths.GEOGRAPHY_SENTIMENT,
        element: <CustomDashRoute idOverride={geographySentiment.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Compositions',
        serverTab: Tab.Overview,
        path: PagePaths.ROLE_COMPOSITIONS,
        element: <CustomDashRoute idOverride={roleComposition.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Transitions',
        serverTab: Tab.Transition,
        path: PagePaths.ROLE_TRANSITIONS,
        element: <CustomDashRoute idOverride={roleTransitions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Job Postings',
        serverTab: Tab.Posting,
        path: PagePaths.ROLE_POSTINGS,
        element: <CustomDashRoute idOverride={rolePostings.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Pay',
        serverTab: Tab.Compensation,
        path: PagePaths.ROLE_PAY,
        element: <CustomDashRoute idOverride={rolePay.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Market Conditions',
        serverTab: { allOf: [Tab.Overview, Tab.Posting] },
        path: PagePaths.ROLE_MARKET_CONDITIONS,
        element: <CustomDashRoute idOverride={roleMarketConditions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Sentiment',
        serverTab: Tab.Sentiment,
        path: PagePaths.ROLE_SENTIMENT,
        element: <CustomDashRoute idOverride={roleSentiment.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      // Skill Compositions is the rail entry above; the other skill views are
      // reached through the page-level tab strip like every other entity.
      {
        name: 'Skill Job Postings',
        serverTab: Tab.Posting,
        path: PagePaths.SKILL_POSTINGS,
        element: <CustomDashRoute idOverride={skillPostings.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Skill Pay',
        serverTab: Tab.Compensation,
        path: PagePaths.SKILL_PAY,
        element: <CustomDashRoute idOverride={skillPay.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Skill Market Conditions',
        serverTab: { allOf: [Tab.Overview, Tab.Posting] },
        path: PagePaths.SKILL_MARKET_CONDITIONS,
        element: <CustomDashRoute idOverride={skillMarketConditions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      // Activity views, reached through the page-level tab strip like Skill.
      {
        name: 'Activity Job Postings',
        serverTab: Tab.Posting,
        path: PagePaths.ACTIVITY_POSTINGS,
        element: <CustomDashRoute idOverride={activityPostings.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Activity Pay',
        serverTab: Tab.Compensation,
        path: PagePaths.ACTIVITY_PAY,
        element: <CustomDashRoute idOverride={activityPay.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Activity Market Conditions',
        serverTab: { allOf: [Tab.Overview, Tab.Posting] },
        path: PagePaths.ACTIVITY_MARKET_CONDITIONS,
        element: <CustomDashRoute idOverride={activityMarketConditions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
    ],
  },
  {
    name: PageTitles.DISCOVERY_NAV,
    icon: <IconDiscoveryCompass />,
    isSourcesTab: true,
    pageVisit: 'self',
    children: [
      {
        name: 'Individual',
        serverTab: Tab.TalentDiscovery,
        path: 'talent-discovery',
        element: <CustomDashRoute idOverride={talentDiscovery.id} />,
        parent: PageTitles.DISCOVERY_NAV,
      },
      {
        name: 'Postings',
        // TODO(posting-discovery): swap to Tab.PostingDiscovery once backend
        // ships the enum value. Until then, piggyback on TalentDiscovery for
        // permission gating.
        serverTab: Tab.TalentDiscovery,
        path: PagePaths.POSTING_DISCOVERY,
        element: <CustomDashRoute idOverride={postingDiscovery.id} />,
        parent: PageTitles.DISCOVERY_NAV,
      },
      {
        name: 'Company',
        serverTab: Tab.Screener,
        path: PagePaths.COMPANY_SCREENER,
        element: <CustomDashRoute idOverride={companyScreener.id} />,
        parent: PageTitles.DISCOVERY_NAV,
      },
      {
        name: 'Geography',
        serverTab: Tab.Screener,
        path: PagePaths.GEOGRAPHY_SCREENER,
        element: <CustomDashRoute idOverride={geographyScreener.id} />,
        parent: PageTitles.DISCOVERY_NAV,
      },
      {
        name: 'Role',
        serverTab: Tab.Screener,
        path: PagePaths.ROLE_SCREENER,
        element: <CustomDashRoute idOverride={roleScreener.id} />,
        parent: PageTitles.DISCOVERY_NAV,
      },
    ],
  },
  {
    name: PageTitles.APPLICATIONS_NAV,
    icon: <IconApplicationsGrid />,
    isSourcesTab: true,
    children: [
      {
        name: PageTitles.DATA_ENRICHMENT,
        serverTab: Tab.DataEnrichment,
        path: `${PagePaths.DELIVERABLES}/${PagePaths.DATA_ENRICHMENT}`,
        element: <DeliverablesDataEnrichment />,
      },
      {
        name: PageTitles.RESUME_ENRICHMENT,
        serverTab: Tab.ResumeParsing,
        path: PagePaths.RESUME_ENRICHMENT,
        element: <Outlet />,
        nestedRoutes: [
          {
            path: ':id',
            element: lazyRouteElement(EnrichedResumePage),
            serverTab: Tab.ResumeParsing,
            parent: PageTitles.APPLICATIONS_NAV,
          },
          {
            path: '',
            element: lazyRouteElement(ResumeEnrichment),
            serverTab: Tab.ResumeParsing,
            parent: PageTitles.APPLICATIONS_NAV,
          },
        ],
      },
      {
        // Nav-only link. It lives in navItemsNewLayout, and next.tsx filters it
        // out of the side-nav when the economic-impact-new-layout-app flag is
        // off (filterEconomicImpactFromNav). The route itself is declared
        // explicitly in next.tsx — gated on the same flag — so this entry
        // deliberately has NO `element` (reducedRoutes would otherwise emit a
        // second route). `icon` is required for a tab-less leaf to pass
        // filterNavItemsForUser; sub-nav children render the name, not the icon.
        name: PageTitles.ECONOMIC_IMPACT,
        path: 'economic-impact',
        icon: <IconApplicationsGrid />,
      },
    ],
  },
  {
    name: PageTitles.REPORTS_NAV,
    icon: <IconReportsStack />,
    isSourcesTab: true,
    children: [
      {
        name: PageTitles.REPORT_BUILDER,
        path: PagePaths.REPORT_BUILDER,
        serverTab: Tab.Reports,
        element: lazyRouteElement(ReportBuilderPage),
        // The steps arrive with the section's chunk, under its LazyRoute.
        nestedRoutes: [
          {
            path: ReportBuilderDeliverablePaths.REPORT_TYPE,
            element: <ReportType />,
          },
          {
            path: ReportBuilderDeliverablePaths.ENTITY,
            element: <EntityConfiguration />,
          },
          {
            path: ReportBuilderDeliverablePaths.CONFIRMATION,
            element: <ReportConfirmation />,
          },
        ],
      },
      {
        name: PageTitles.REPORTS_STATUS,
        path: PagePaths.REPORTS,
        serverTab: Tab.Reports,
        element: <Outlet />,
        nestedRoutes: [
          {
            path: '',
            element: <ReportsPage />,
            serverTab: [Tab.Deliverables, Tab.Reports],
            parent: PageTitles.REPORTS_NAV,
          },
          {
            path: ':id',
            element: <ReportPage />,
            serverTab: [Tab.Deliverables, Tab.Reports],
            parent: PageTitles.REPORTS_NAV,
          },
        ],
      },
      {
        name: PageTitles.REPORTS_SAMPLE,
        path: PagePaths.REPORTS_SAMPLE,
        // Reports the sampled report's id alongside the visit.
        pageVisit: 'self',
        element: <SampleReportPage />,
      },
    ],
  },
  {
    name: PageTitles.DELIVERABLES,
    icon: <IconDataBuilder />,
    isSourcesTab: true,
    divider: true,
    children: [
      {
        name: PageTitles.DATA_BUILDER,
        path: PagePaths.DATA_BUILDER,
        serverTab: Tab.Deliverables,
        element: (
          <DataBuilderRouteGuard>
            {lazyRouteElement(DataBuilder)}
          </DataBuilderRouteGuard>
        ),
        // The steps arrive with the section's chunk, under its LazyRoute.
        nestedRoutes: [
          {
            path: DataBuilderPaths.CHOOSE,
            element: <DataBuilderChooseStep />,
          },
          {
            path: DataBuilderPaths.SAVED_DATASETS,
            element: <DataBuilderSavedDatasetsStep />,
          },
          {
            path: DataBuilderPaths.DATASET,
            element: <DataBuilderDatasetStep />,
          },
          {
            path: DataBuilderPaths.COLUMNS,
            element: <DataBuilderColumnsStep />,
          },
          {
            path: DataBuilderPaths.DELIVERY,
            element: <DataBuilderDeliveryStep />,
          },
          {
            path: DataBuilderPaths.CONFIRMATION,
            element: <DataBuilderConfirmationStep />,
          },
        ],
      },
      {
        name: PageTitles.DELIVERABLES_STATUS,
        serverTab: Tab.Deliverables,
        path: PagePaths.DATA_BUILDER_STATUS,
        element: (
          <DataBuilderRouteGuard>
            {lazyRouteElement(DataBuilderStatus)}
          </DataBuilderRouteGuard>
        ),
      },
      {
        name: 'Data Dictionary',
        path: `${PagePaths.DATA_DICTIONARY}/*`,
        serverTab: Tab.DataDictionary,
        // A visit per page of the iframed docs, which the route can't see.
        pageVisit: 'self',
        element: <DataDictionary />,
      },
      {
        name: 'Shared Delivery',
        serverTab: Tab.Deliverables,
        path: `${PagePaths.DATA_BUILDER_SHARE}/:shareToken`,
        excludeFromSideNavButtons: true,
        element: (
          <DataBuilderRouteGuard>
            {lazyRouteElement(DataBuilderShareRedeem)}
          </DataBuilderRouteGuard>
        ),
      },
    ],
  },
  ...sharedAppRoutesForNav,
];
